import React, { type ReactNode } from "react";
import { Error } from "../Error";

import { concatStringFromClassesArrayList, sanitiseString } from "../../Utils";

export interface Props {
  id?: string;
  classes?: string;
  legend?: string;
  legendClasses?: string;
  legendIsQuestionTitle?: boolean;
  legendTitleClasses?: string;
  description?: string;
  attributes?: { [key: string]: string };
  error?: React.ComponentProps<typeof Error>;
  dontWrap?: boolean;
  dataTestId: string;
  children: ReactNode;
}

export const Fieldset = (props: Props) => {
  const sanitisedId: string = sanitiseString(props.id);
  const sanitisedDataTestId: string = sanitiseString(props.dataTestId);

  const renderFieldset = () => {
    if (props.dontWrap) {
      return (
        <div className="ons-input-items" data-testid={sanitisedDataTestId}>
          {props.children}
        </div>
      );
    } else if (props.legend || props.legendIsQuestionTitle) {
      let descriptionID: string;

      if (sanitisedId) {
        descriptionID = `${sanitisedId}-legend-description`;
      } else {
        descriptionID = "legend-description";
      }

      const fieldClassList = {
        defaultClass: "ons-fieldset",
        optionalClasses: [props.classes || null],
      };
      const fieldClassName = concatStringFromClassesArrayList(fieldClassList);

      const legendClassList = {
        defaultClass: "ons-fieldset__legend",
        optionalClasses: [
          props.legendIsQuestionTitle ? "ons-u-mb-no" : null,
          props.legendClasses || null,
          props.description ? "ons-fieldset__legend--with-description" : null,
        ],
      };
      const legendClassName = concatStringFromClassesArrayList(legendClassList);

      const legendTitleClassList = {
        defaultClass: "ons-fieldset__legend-title",
        optionalClasses: [props.legendTitleClasses || null],
      };
      const legendTitleClassName =
        concatStringFromClassesArrayList(legendTitleClassList);

      const descriptionClassList = {
        defaultClass: "ons-fieldset__description",
        optionalClasses: [
          props.legendIsQuestionTitle
            ? "ons-fieldset__description--title ons-u-mb-m"
            : null,
        ],
      };
      const descriptionClassName =
        concatStringFromClassesArrayList(descriptionClassList);

      return (
        <fieldset
          {...(sanitisedId ? { id: sanitisedId } : {})}
          className={
            !props.error ? fieldClassName : fieldClassList.defaultClass
          }
          data-testid={sanitisedDataTestId}
          {...(props.attributes
            ? Object.entries(props.attributes)
                .map(([attribute, value]) => ({ [attribute]: value }))
                .reduce((acc, attribute) => ({ ...acc, ...attribute }), {})
            : {})}
        >
          <legend
            {...(props.description
              ? { "aria-describedby": descriptionID }
              : {})}
            className={legendClassName}
            data-testid={`${sanitisedDataTestId}-legend`}
          >
            {props.legendIsQuestionTitle ? (
              <h1 id="fieldset-legend-title" className={legendTitleClassName}>
                {props.legend}
              </h1>
            ) : (
              <span className="ons-fieldset__legend-title ons-u-pb-no">
                {props.legend}
              </span>
            )}
          </legend>
          {props.description ? (
            <div
              {...(props.description ? { id: descriptionID } : {})}
              className={descriptionClassName}
              data-testid={`${sanitisedDataTestId}-description`}
            >
              {props.description}
            </div>
          ) : (
            ""
          )}
          {props.children}
        </fieldset>
      );
    }
    return <div />;
  };

  return props.error ? (
    <Error
      text={props.error.text}
      id={sanitisedId || "fieldset-error-id"}
      dataTestId={`${sanitisedDataTestId}-error`}
      classes={props.classes}
    >
      {renderFieldset()}
    </Error>
  ) : (
    renderFieldset()
  );
};
